import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Package, CheckCircle, ArrowRight } from "lucide-react";

const RiskManagementService = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Risk Management Services | 4FOX Security"
        description="Comprehensive cyber risk assessments using ISO 27005 and NIST frameworks. Identify vulnerabilities, assess impacts, and implement appropriate controls."
        canonical="https://4fox.uk/services/risk-management"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Risk Management Services",
          "provider": {
            "@type": "Organisation",
            "name": "4FOX Security"
          },
          "serviceType": "Cyber Security Risk Assessment",
          "areaServed": "GB"
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Risk Management</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Identify, assess, and mitigate cyber risks to protect your organisation's critical assets.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Comprehensive Risk Assessment</h2>
              <p className="mb-6">
                Effective cyber security begins with understanding your risks. Our risk management services help you identify vulnerabilities, assess potential impacts, and implement appropriate controls to protect your organisation from cyber threats. We use ISO 27005 and NIST frameworks to conduct quantitative risk assessments that provide clear, actionable insights.
              </p>

              <h2 className="text-2xl font-bold mb-4">Assessment Scope & Timeline</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>2-4 Week Engagement covering:</strong></p>
                <ul className="space-y-2">
                  <li>• <strong>People:</strong> Role-based access reviews and insider threat assessment</li>
                  <li>• <strong>Processes:</strong> Policy gaps, incident response readiness, business continuity</li>
                  <li>• <strong>Technology:</strong> Infrastructure vulnerabilities, cloud security, network architecture</li>
                  <li>• <strong>Third Parties:</strong> Supply chain and vendor risk evaluation</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">What You'll Receive</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Risk register</strong> with 20-50 identified risks, prioritised by likelihood and impact</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Risk heat maps</strong> providing visual representation of your threat landscape</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Treatment plans</strong> with detailed cost-benefit analysis for each risk</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Investment roadmap</strong> prioritizing security spending based on actual risk reduction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Executive summary</strong> communicating risks and recommendations to leadership</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Industry-Specific Expertise</h2>
              <div className="space-y-4 mb-6">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Financial Services:</p>
                  <p className="text-muted-foreground">Focus on data protection, fraud prevention, and regulatory compliance (FCA, PCI-DSS)</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Manufacturing:</p>
                  <p className="text-muted-foreground">OT/IT convergence risks, supply chain security, and intellectual property protection</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Healthcare:</p>
                  <p className="text-muted-foreground">Patient data protection, medical device security, and GDPR compliance</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Measurable Outcomes</h2>
              <div className="bg-accent/10 rounded-lg p-6">
                <p className="mb-3">Organisations typically identify:</p>
                <ul className="space-y-2">
                  <li>• <strong>£250K-£2M in potential loss exposure</strong> from previously unidentified risks</li>
                  <li>• <strong>20-30% more efficient security investment</strong> by prioritizing based on actual risk</li>
                  <li>• <strong>Clear justification</strong> for security budget increases to executive teams</li>
                  <li>• <strong>Improved cyber insurance terms</strong> through demonstrated risk management</li>
                </ul>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to understand and manage your cyber risks?</h3>
              <p className="text-muted-foreground mb-6">
                Let's develop a comprehensive risk management program tailored to your organisation.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RiskManagementService;
